import { useEffect, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';
import { CustomInput } from 'components/UI/CustomInput';
import { CONTACT_INQUIRY_MAX_LENGTHS } from 'features/Auth/utils/contactInquiry.utils';

export default function ContactUs({
  contactName,
  setContactName,
  contactOrganizationName,
  setContactOrganizationName,
  contactEmail,
  setContactEmail,
  contactPhone,
  setContactPhone,
  contactReason,
  setContactReason,
  contactNotes,
  setContactNotes,
  onSubmit,
  onBack,
  hasRequiredFields = false,
  submitting = false,
  success = false,
  error = null,
}) {
  const thanksHeadingRef = useRef(null);

  useEffect(() => {
    if (success) thanksHeadingRef.current?.focus();
  }, [success]);

  if (success) {
    return (
      <div className="text-center">
        <div role="status">
          <h2 className="mb-2" ref={thanksHeadingRef} tabIndex={-1}>
            Thanks!
          </h2>
          <p className="mb-4">We received your message and we’ll be in touch.</p>
        </div>
        <Button onClick={onBack} className="rounded-4 w-100 py-3">
          Back to Sign In
        </Button>
      </div>
    );
  }

  return (
    <Form
      className="w-100 p-3"
      style={{ maxWidth: 380 }}
      aria-label="Contact us"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="mb-1">
        <h1 className="mb-2">Welcome!</h1>
        <div style={{ color: '#A0AEC0' }}>Contact us!</div>
      </div>
      <div className="mb-3">
        <Form.Label htmlFor="contact-name" className="py-1 mx-2">
          Name (required)
        </Form.Label>

        <CustomInput
          id="contact-name"
          name="name"
          required
          autoComplete="name"
          value={contactName}
          callback={setContactName}
          maxLength={CONTACT_INQUIRY_MAX_LENGTHS.name}
          type="text"
          placeholder="Your name"
        />
      </div>

      <div className="mb-3">
        <Form.Label htmlFor="contact-organization" className="py-1 mx-2">
          Organization Name (required)
        </Form.Label>

        <CustomInput
          id="contact-organization"
          name="organizationName"
          required
          autoComplete="organization"
          value={contactOrganizationName}
          callback={setContactOrganizationName}
          maxLength={CONTACT_INQUIRY_MAX_LENGTHS.organizationName}
          type="text"
          placeholder="Organization name"
        />
      </div>

      <div className="mb-3">
        <Form.Label htmlFor="contact-phone" className="py-1 mx-2">
          Phone Number (required)
        </Form.Label>

        <CustomInput
          id="contact-phone"
          name="phone"
          required
          autoComplete="tel"
          value={contactPhone}
          callback={setContactPhone}
          maxLength={CONTACT_INQUIRY_MAX_LENGTHS.phone}
          type="tel"
          placeholder="(555)555-5555"
        />
      </div>

      <div className="mb-3">
        <Form.Label htmlFor="contact-email" className="py-1 mx-2">
          Email (required)
        </Form.Label>

        <CustomInput
          id="contact-email"
          name="email"
          required
          autoComplete="email"
          value={contactEmail}
          callback={setContactEmail}
          maxLength={CONTACT_INQUIRY_MAX_LENGTHS.email}
          type="email"
          placeholder="Email Address"
        />
      </div>

      <div className="mb-3">
        <Form.Label htmlFor="contact-reason" className="py-1 mx-2">
          Reason for Inquiry (required)
        </Form.Label>

        <CustomInput
          id="contact-reason"
          name="reasonForInquiry"
          required
          value={contactReason}
          callback={setContactReason}
          maxLength={CONTACT_INQUIRY_MAX_LENGTHS.reason}
          type="text"
          placeholder="e.g., Sales or Organization Onboarding"
        />
      </div>

      <div className="mb-3">
        <Form.Label htmlFor="contact-notes" className="py-1 mx-2">
          Notes (optional)
        </Form.Label>

        <CustomInput
          id="contact-notes"
          name="notes"
          value={contactNotes}
          callback={setContactNotes}
          maxLength={CONTACT_INQUIRY_MAX_LENGTHS.notes}
          textarea
          rows={4}
          placeholder="Enter any additional information"
        />
      </div>

      {error && (
        <div className="mt-2 text-danger" role="alert">
          {error}
        </div>
      )}

      <Button
        type="submit"
        className="w-100 rounded-4 py-3 mb-3"
        disabled={submitting || !hasRequiredFields}
      >
        {submitting ? 'Sending...' : 'Send'}
      </Button>

      <div className="text-center">
        <button
          type="button"
          className="btn btn-link text-decoration-none px-0"
          style={{ color: 'var(--color-font)', opacity: 0.8 }}
          onClick={onBack}
        >
          ← Back to Sign In
        </button>
      </div>
    </Form>
  );
}
